import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  List,
  ListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const TermsOfServiceDialog = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
      aria-labelledby="terms-of-service-dialog-title"
    >
      <DialogTitle
        id="terms-of-service-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" component="h2">
          Terms of Service
        </Typography>
        <IconButton aria-label="close" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>

        {/* Section 1: Introduction */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            1. Introduction
          </Typography>
          <Typography paragraph>
            Welcome to our AI-powered hiring platform. These Terms of Service
            ("Terms") govern your access to and use of our website,
            applications, and services (collectively, the "Service"). By
            accessing or using the Service, you agree to be bound by these Terms
            and our Privacy Policy.
          </Typography>
          <Typography paragraph>
            If you are using the Service on behalf of a company or other legal
            entity, you represent that you have the authority to bind such
            entity to these Terms. If you do not have such authority, or if you
            do not agree with these Terms, you must not accept these Terms and
            may not use the Service.
          </Typography>
        </section>

        {/* Section 2: Definitions */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            2. Definitions
          </Typography>
          <Typography paragraph>In these Terms:</Typography>
          <List sx={{ pl: 2 }}>
            <ListItem>
              "We," "us," and "our" refer to the company operating the Service.
            </ListItem>
            <ListItem>
              "You" and "your" refer to the individual or entity using the
              Service.
            </ListItem>
            <ListItem>
              "Content" refers to all text, information, data, logos, marks,
              designs, graphics, pictures, sound files, other files, and their
              selection and arrangement.
            </ListItem>
            <ListItem>
              "User Content" refers to any Content that you submit, post, or
              otherwise make available to the Service.
            </ListItem>
            <ListItem>
              "AI" refers to artificial intelligence systems used in our
              platform.
            </ListItem>
          </List>
        </section>

        {/* Section 3: Account Registration */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            3. Account Registration and Eligibility
          </Typography>
          <Typography paragraph>
            To access certain features of the Service, you must register for an
            account. You agree to provide accurate, current, and complete
            information during the registration process and to update such
            information as needed.
          </Typography>
          <Typography paragraph>
            You are responsible for safeguarding your password and for all
            activities that occur under your account. You agree to notify us
            immediately of any unauthorized use of your account.
          </Typography>
          <Typography paragraph>
            To use the Service, you must be at least 18 years of age and have
            the legal capacity to enter into binding contracts. By using the
            Service, you represent and warrant that you meet these requirements.
          </Typography>
        </section>

        {/* Section 4: Subscription and Payment */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            4. Subscription and Payment Terms
          </Typography>
          <Typography paragraph>
            The Service offers various subscription plans. By selecting a
            subscription plan, you agree to pay the applicable fees as they
            become due.
          </Typography>
          <Typography paragraph>
            Subscription fees are billed in advance on a monthly or annual
            basis, depending on your selected billing cycle. You authorize us to
            charge your designated payment method for all fees incurred.
          </Typography>
        </section>
        {/* Section 5: User Content */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            5. User Content and Licenses
          </Typography>
          <Typography paragraph>
            You retain all rights in the User Content you submit, post, or
            display on or through the Service. By submitting User Content, you
            grant us a worldwide, non-exclusive, royalty-free license to use,
            copy, modify, create derivative works based on, distribute, publicly
            display, and publicly perform your User Content for the purposes of
            operating and improving the Service.
          </Typography>
          <Typography paragraph>
            You represent and warrant that: (i) you own the User Content
            submitted by you or have the right to grant the license set forth
            above, and (ii) the User Content does not violate the privacy
            rights, publicity rights, intellectual property rights, or any other
            rights of any person.
          </Typography>
        </section>

        {/* Section 6: Intellectual Property */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            6. Intellectual Property Rights
          </Typography>
          <Typography paragraph>
            The Service and its original content, features, and functionality
            are and will remain the exclusive property of our company and its
            licensors. The Service is protected by copyright, trademark, and
            other laws of both the United States and foreign countries. Our
            trademarks and trade dress may not be used in connection with any
            product or service without our prior written consent.
          </Typography>
          <Typography paragraph>
            You acknowledge that the algorithms, structure, organization, and
            code of our AI systems are valuable trade secrets and confidential
            information of our company. Nothing in these Terms grants you any
            rights to use our trademarks, logos, domain names, or other
            distinctive brand features.
          </Typography>
        </section>

        {/* Section 7: Acceptable Use */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            7. Acceptable Use Policy
          </Typography>
          <Typography paragraph>
            You agree not to use the Service for any purpose that is prohibited
            by these Terms or applicable law. You are responsible for all of
            your activity in connection with the Service.
          </Typography>
          <Typography paragraph>
            You shall not (and shall not permit any third party to): (a) take
            any action that imposes an unreasonable load on our infrastructure;
            (b) use any device, software, or routine to interfere with the
            proper working of the Service; (c) attempt to decipher, decompile,
            disassemble, or reverse engineer any of the software comprising the
            Service; (d) delete or alter any material we or any other person or
            entity posts; or (e) use the Service for any unlawful purpose or in
            furtherance of illegal activities.
          </Typography>
          <Typography paragraph>
            You further agree not to use the Service to: (a) harass, abuse, or
            harm another person; (b) impersonate or misrepresent your
            affiliation with any person or entity; (c) upload or transmit
            viruses or other malicious code; (d) interfere with or circumvent
            security features of the Service; or (e) collect or harvest any
            personally identifiable information from the Service.
          </Typography>
        </section>

        {/* Section 8: AI Usage */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            8. AI System Usage and Limitations
          </Typography>
          <Typography paragraph>
            Our Service incorporates AI systems to assist in the hiring process.
            You acknowledge that these AI systems are designed to be tools to
            aid human decision-making and are not intended to replace human
            judgment in hiring decisions.
          </Typography>
          <Typography paragraph>
            While we strive to minimize biases in our AI systems, you
            acknowledge that AI technologies have inherent limitations. You
            agree to review AI-generated recommendations and not to rely solely
            on them for making employment decisions. You retain full
            responsibility for all hiring decisions made using our Service.
          </Typography>
          <Typography paragraph>
            You agree not to attempt to manipulate our AI systems to generate
            discriminatory outcomes or to circumvent fair hiring practices and
            applicable employment laws.
          </Typography>
        </section>

        {/* Section 9: Disclaimer */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            9. Disclaimer of Warranties
          </Typography>
          <Typography paragraph>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
            WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT
            NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
            SERVICE WILL FUNCTION UNINTERRUPTED, SECURE, OR AVAILABLE AT ANY
            PARTICULAR TIME OR LOCATION, OR THAT ANY ERRORS OR DEFECTS WILL BE
            CORRECTED.
          </Typography>
          <Typography paragraph>
            WE DO NOT WARRANT OR MAKE ANY REPRESENTATIONS REGARDING THE USE OF
            THE SERVICE IN TERMS OF ACCURACY, RELIABILITY, OR OTHERWISE. YOU ARE
            SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM OR LOSS OF
            DATA THAT RESULTS FROM THE DOWNLOAD OF ANY CONTENT, MATERIALS,
            INFORMATION, OR SOFTWARE.
          </Typography>
        </section>

        {/* Section 10: Limitation of Liability */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            10. Limitation of Liability
          </Typography>
          <Typography paragraph>
            IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT
            LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER
            INTANGIBLE LOSSES, RESULTING FROM (A) YOUR ACCESS TO OR USE OF OR
            INABILITY TO ACCESS OR USE THE SERVICE; (B) ANY CONDUCT OR CONTENT
            OF ANY THIRD PARTY ON THE SERVICE; (C) ANY CONTENT OBTAINED FROM THE
            SERVICE; AND (D) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR
            TRANSMISSIONS OR CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, TORT
            (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE
            HAVE BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE.
          </Typography>
          <Typography paragraph>
            IN JURISDICTIONS WHERE THE EXCLUSION OR LIMITATION OF LIABILITY FOR
            CONSEQUENTIAL OR INCIDENTAL DAMAGES IS NOT ALLOWED, OUR LIABILITY
            SHALL BE LIMITED TO THE MAXIMUM EXTENT PERMITTED BY LAW.
          </Typography>
        </section>

        {/* Section 11: Termination */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            11. Term and Termination
          </Typography>
          <Typography paragraph>
            These Terms shall remain in full force and effect while you use the
            Service. We may terminate or suspend your account and access to the
            Service immediately, without prior notice or liability, at our sole
            discretion, for any reason whatsoever, including without limitation
            if you breach these Terms.
          </Typography>
          <Typography paragraph>
            Upon termination, your right to use the Service will immediately
            cease. If you wish to terminate your account, you may simply
            discontinue using the Service or delete your account through the
            account settings.
          </Typography>
          <Typography paragraph>
            All provisions of these Terms which by their nature should survive
            termination shall survive termination, including, without
            limitation, ownership provisions, warranty disclaimers, indemnity,
            and limitations of liability.
          </Typography>
        </section>

        {/* Section 12: Governing Law */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            12. Governing Law and Dispute Resolution
          </Typography>
          <Typography paragraph>
            These Terms shall be governed by and construed in accordance with
            the laws of the state of [State], without regard to its conflict of
            law provisions.
          </Typography>
          <Typography paragraph>
            Any dispute arising from or relating to the subject matter of these
            Terms shall be finally settled by arbitration in [City, State],
            using the English language in accordance with the rules and
            procedures of the American Arbitration Association. Judgment upon
            the award rendered by the arbitrator(s) may be entered in any court
            having jurisdiction thereof.
          </Typography>
          <Typography paragraph>
            Notwithstanding the foregoing, we may seek and obtain injunctive
            relief in any jurisdiction in any court of competent jurisdiction,
            and you agree that these Terms are specifically enforceable by us
            through injunctive relief and other equitable remedies without proof
            of monetary damages.
          </Typography>
        </section>

        {/* Section 13: Changes to Terms */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            13. Changes to Terms
          </Typography>
          <Typography paragraph>
            We reserve the right, at our sole discretion, to modify or replace
            these Terms at any time. We will provide notice of any changes by
            posting the new Terms on the Service and updating the "Last Updated"
            date.
          </Typography>
          <Typography paragraph>
            Your continued use of the Service after any such changes constitutes
            your acceptance of the new Terms. If you do not agree to the revised
            Terms, please stop using the Service.
          </Typography>
        </section>

        {/* Section 14: Contact Information */}
        <section>
          <Typography variant="h6" sx={{ mb: 2 }}>
            14. Contact Information
          </Typography>
          <Typography paragraph>
            If you have any questions about these Terms, please contact us.
          </Typography>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default TermsOfServiceDialog;
